import { useMemo } from "react";

import { ComboBoxDropDownItem } from "@/Common/Components/Inputs/Combobox";
import { ComboBoxControl } from "@/Common/Components/Inputs/ComboboxControl";
import { EnumHelper } from "@/Common/Helpers/EnumHelper";
import { ServiceComboTargetClientele } from "@/Common/Models/Services/Enums/ServiceComboTargetClientele";
import { ServiceDefinitionModel } from "@/Common/Models/Services/ServiceDefinitionModel";

import styles from "./EditServiceOptions.module.scss";

interface EditServiceOptionsProps {
    /** The selected service information. */
    selectedService: ServiceDefinitionModel;

    /** Returns the new boolean value whenever user clicks on is online checkbox. */
    onIsOnlineChange: (isOnline: boolean) => void

    /** Returns the new target client whenever user switch to another targeted client. */
    onTargetedClientChange: (targetedClient: ServiceComboTargetClientele) => void
}

function getTargetClienteleOptions(): ComboBoxDropDownItem[] {
    const options = [
        new ComboBoxDropDownItem(
            EnumHelper.getName(ServiceComboTargetClientele, ServiceComboTargetClientele.AssignedClientele),
            cacheStore.GetString("AssignedLabel")
        )
    ];

    if (cacheStore.ClinicSettings.EnableBookingAlgorithmSiteAndOrganizationTargetOptions) {
        if (cacheStore.SiteCache.length > 1)
            options.push(new ComboBoxDropDownItem(
                EnumHelper.getName(ServiceComboTargetClientele, ServiceComboTargetClientele.Site),
                cacheStore.GetString("SiteDLabel")
            ));

        if (cacheStore.ParentSiteCache.length > 1)
            options.push(new ComboBoxDropDownItem(
                EnumHelper.getName(ServiceComboTargetClientele, ServiceComboTargetClientele.Organization),
                cacheStore.GetString("OrganizationLabel")
            ));
    }

    options.push(
        new ComboBoxDropDownItem(
            EnumHelper.getName(ServiceComboTargetClientele, ServiceComboTargetClientele.Clinic),
            cacheStore.GetString("MyleEnvironnementLabel")
        )
    );

    options.push(
        new ComboBoxDropDownItem(
            EnumHelper.getName(ServiceComboTargetClientele, ServiceComboTargetClientele.Open),
            cacheStore.GetString("IsOpenLabel")
        )
    );

    return options;
}

function getServiceNameAndDuration(service: ServiceDefinitionModel): string {
    return `${cacheStore.GetLocalizedValueByLookupId(service.Model.SpecializationLookupId)} - ${service.Name} - (${service.GetDefaultDurationMinutes()} min.)`;
}

function EditServiceOptions(props: EditServiceOptionsProps): JSX.Element {
    const targetClienteleList = useMemo<ComboBoxDropDownItem[]>(() => getTargetClienteleOptions(), []);
    const serviceNameWithMinutes = useMemo<string>(() => getServiceNameAndDuration(props.selectedService), [props.selectedService]);

    function handleOnIsOnlineChange(isChecked: boolean): void {
        props.onIsOnlineChange(isChecked);
    }

    function handleOnTargetedClientChange(targetClient: string): void {
        props.onTargetedClientChange(EnumHelper.getValue(ServiceComboTargetClientele, targetClient));
    }

    return (
        <section className="display-flex-colunm center">
            <header className={`flex-container ${styles.editServiceOptionsHeader}`}>
                <div className="scheduling-col scheduling-col-service" />
                <div className="scheduling-col scheduling-col-online">
                    <label htmlFor="EditServiceisOnline">
                        {cacheStore.GetString("PortalLabel")}
                    </label>
                </div>
                {cacheStore.ClinicSettings.EnableIsTeleconsultationCheckbox && (
                    <div className="scheduling-col scheduling-col-teleconsultation">
                        <label htmlFor="EditServiceIsTeleconsultation">
                            {cacheStore.GetString("TeleconsultationAbbrevLabel")}
                        </label>
                    </div>
                )}
                <div className="scheduling-col scheduling-col-customerbase">
                    <label htmlFor="EditServiceClientComboBox">
                        {cacheStore.GetString("ClienteleLabel")}
                    </label>
                </div>
            </header>
            <div className={`flex-container ${styles.editServiceOptionsRow}`}>
                <div className="scheduling-col scheduling-col-service flex-container left">
                    <strong className="underline">{serviceNameWithMinutes}</strong>
                </div>
                <div className="scheduling-col scheduling-col-online">
                    <input
                        id="EditServiceisOnline"
                        type="checkbox"
                        checked={props.selectedService.Model.IsAvailableOnline}
                        onChange={e => handleOnIsOnlineChange(e.target.checked)}
                    />
                </div>
                {cacheStore.ClinicSettings.EnableIsTeleconsultationCheckbox && (
                    <div className="scheduling-col scheduling-col-teleconsultation">
                        <span id="EditServiceIsTeleconsultation" className={styles.editServiceOptionsTeleconsultationIcon}>
                            {props.selectedService.Model.IsTeleconsultation &&
                                <i className="fi-video col-lg-icon" />
                            }
                        </span>
                    </div>
                )}
                <div className="scheduling-col scheduling-col-customerbase">
                    <ComboBoxControl
                        hasEmptyItem={false}
                        id="EditServiceClientComboBox"
                        items={Immutable.List(targetClienteleList)}
                        selectedCode={EnumHelper.getName(ServiceComboTargetClientele, props.selectedService.Model.OnlineClienenteleCible)}
                        onChange={handleOnTargetedClientChange}
                    />
                </div>
            </div>
        </section>
    );
}

export { EditServiceOptions };
